import { ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PlatformIcon } from '@/components/ui/platform-icon';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export const TWITCH_SCOPES = [
  'user:read:chat',
  'user:write:chat',
  'moderator:manage:banned_users',
  'moderator:manage:chat_messages',
];

const YOUTUBE_SCOPES = ['youtube.force-ssl (Live Chat: list/ban/delete/insert)'];

export interface PlatformConnectionView {
  platformKey: 'twitch' | 'youtube';
  name: 'Twitch' | 'YouTube';
  connected: boolean;
  connectedAs?: string;
  scopes: string[];
}

interface ConnectCardProps {
  connections: PlatformConnectionView[];
  onConnect: (platform: 'twitch' | 'youtube') => void;
  onDisconnect: (platform: 'twitch' | 'youtube') => void;
}

function PlatformRow({
  conn,
  onConnect,
  onDisconnect,
}: {
  conn: PlatformConnectionView;
  onConnect: (p: 'twitch' | 'youtube') => void;
  onDisconnect: (p: 'twitch' | 'youtube') => void;
}) {
  const isConnected = conn.connected;
  return (
    <div
      className={
        'flex items-center justify-between gap-4 p-4 rounded-md border ' +
        (isConnected
          ? 'bg-bg-elevated border-success/40'
          : 'bg-bg-card border-border-focus shadow-[0_0_0_3px_hsl(var(--primary-soft))]')
      }
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          aria-hidden="true"
          className={
            'h-9 w-9 rounded-md flex items-center justify-center ' +
            (conn.platformKey === 'twitch'
              ? 'bg-platform-twitch-soft text-platform-twitch'
              : 'bg-platform-youtube-soft text-platform-youtube')
          }
        >
          <PlatformIcon platform={conn.platformKey} size={18} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold">{conn.name}</span>
            {isConnected ? <Badge variant="success">conectado</Badge> : <Badge variant="info">pendente</Badge>}
          </div>
          <p className="text-xs text-fg-muted mt-0.5 truncate">
            {isConnected ? `como ${conn.connectedAs}` : 'requisita os scopes abaixo'}
          </p>
        </div>
      </div>
      <Button
        variant={isConnected ? 'outline' : 'primary'}
        onClick={() => (isConnected ? onDisconnect(conn.platformKey) : onConnect(conn.platformKey))}
        aria-label={isConnected ? `Desconectar ${conn.name}` : `Conectar ${conn.name}`}
      >
        {isConnected ? 'Desconectar' : 'Conectar'}
      </Button>
      <span className="sr-only">Scopes: {conn.scopes.join(', ')}</span>
    </div>
  );
}

export function ConnectCard({ connections, onConnect, onDisconnect }: ConnectCardProps) {
  return (
    <Card className="w-full max-w-[520px]">
      <CardHeader>
        {/* #119: título maior (text-2xl = 22px, topo da escala) — mais hierarquia
            no momento de decisão do OAuth. Prototype usa 26px; a escala trava em 22 (P14). */}
        <CardTitle className="text-2xl">Conecte suas plataformas</CardTitle>
        <CardDescription>
          Multimod precisa de acesso aos chats da live e às ações de moderação. Você
          desconecta a qualquer momento.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {connections.map((c) => (
          <PlatformRow key={c.platformKey} conn={c} onConnect={onConnect} onDisconnect={onDisconnect} />
        ))}
        {/* Scopes colapsáveis (#117): ocupavam ~80px verticais sem agregar valor
            no fluxo de "deixe-me logar". Quem quer auditar ainda acessa via
            disclosure. Native <details> = a11y por default + zero JS. */}
        <details className="group pt-2">
          <summary className="flex items-center gap-1.5 cursor-pointer text-xs text-fg-muted hover:text-fg transition-colors duration-fast select-none list-none [&::-webkit-details-marker]:hidden">
            <ChevronDown
              size={14}
              strokeWidth={1.75}
              className="transition-transform duration-fast group-open:rotate-180"
              aria-hidden="true"
            />
            <span>Ver scopes solicitados</span>
          </summary>
          <div className="mt-3 space-y-3">
            <div>
              <p className="text-2xs uppercase tracking-wider font-medium text-platform-twitch mb-1.5">Twitch</p>
              <ul className="text-xs text-fg-subtle space-y-0.5 list-disc list-inside marker:text-fg-subtle/60">
                {TWITCH_SCOPES.map((s) => <li key={`tw-${s}`}>{s}</li>)}
              </ul>
            </div>
            <div>
              <p className="text-2xs uppercase tracking-wider font-medium text-platform-youtube mb-1.5">YouTube</p>
              <ul className="text-xs text-fg-subtle space-y-0.5 list-disc list-inside marker:text-fg-subtle/60">
                {YOUTUBE_SCOPES.map((s) => <li key={`yt-${s}`}>{s}</li>)}
              </ul>
            </div>
          </div>
        </details>
      </CardContent>
      {/* #118: trust strip migrou pro footer full-width da ConnectPage (paridade com
          screen-connect-1.html) — evita duplicar a mesma microcopy no card e na página. */}
    </Card>
  );
}
